'use client';

/**
 * Client-side store that drives the SSR product-listing page from an already-fetched Algolia
 * dataset — NO network calls for filtering, sorting or faceting.
 *
 * The dataset is preloaded ONCE on the server (Redis-cached, from the browse products index so the
 * default order is "new first"), but only its FIRST BATCH (INITIAL_SSR_HITS) is handed to
 * <LocalDatasetProvider> as a prop — enough for the visible grid to render into the SSR HTML without
 * carrying the whole catalogue in it. Immediately after hydration this provider pulls the withheld
 * remainder in a single top-up XHR and concatenates it, after which every refinement is recomputed
 * here in memory exactly as before.
 *
 * So the dataset has two phases, and consumers that care must read `isComplete`:
 *   - incomplete — `hits` is only the first batch; `results.nbHits` undercounts, and facet counts are
 *     computed from that batch alone. `totalHits` is the real size throughout.
 *   - complete   — `hits` is the full result set; everything is authoritative.
 * The window between them is normally a few hundred ms (the top-up reads the warm Redis entry the SSR
 * fetch just populated), but the UI must stay correct when it is not — hence the guards in
 * ProductListSSR, which defers restoring persisted refinements and disables the reveal controls until
 * the set is complete rather than filtering or paginating a partial one.
 *
 * The exported hooks intentionally mirror the signatures of the react-instantsearch-hooks connectors
 * (useHits, useRefinementList, useRange, useCurrentRefinements, useClearRefinements, useSortBy,
 * useInstantSearch) so the existing facet UI can be reused by only repointing its imports here.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import { AnyRecord, LocalDataset, LocalResults, SortMode, queryLocalDataset, valuesAt } from 'helpers/algolia/local-dataset';

// Type-only re-exports so the copied facet components (which imported these alongside the
// InstantSearch hooks) keep compiling after their imports were repointed to this store.
export type { RefinementListProps } from 'react-instantsearch-hooks-web';
export type { UseSortByProps } from 'react-instantsearch-hooks';

interface SortItem {
  label: string;
  value: string; // index name (base = default/new-first, *_name_asc, *_name_desc)
}

interface StoreShape {
  dataset: LocalDataset;
  results: LocalResults;
  /** Size of the FULL result set — correct from first paint, unlike `results.nbHits` while topping up. */
  totalHits: number;
  /** False until the withheld hits have arrived. See the two-phase note at the top of this file. */
  isComplete: boolean;
  refinementList: Record<string, string[]>;
  numericRefinements: Record<string, [number, number]>;
  currentIndex: string;
  baseIndex: string;
  toggleRefinement: (attribute: string, value: string) => void;
  setRefinementList: (next: Record<string, string[]>) => void;
  setNumericRefinement: (attribute: string, range: [number, number]) => void;
  clearNumericRefinement: (attribute: string) => void;
  clearAll: () => void;
  setCurrentIndex: (indexName: string) => void;
}

const EMPTY_DATASET: LocalDataset = { hits: [], facetNames: [] };

const StoreContext = createContext<StoreShape | null>(null);

/** Map an index name to a sort mode, relative to the base (default/new-first) index. */
const indexToSortMode = (indexName: string, baseIndex: string): SortMode => {
  if (!indexName || indexName === baseIndex) return 'default';
  if (indexName.endsWith('_name_asc')) return 'name_asc';
  if (indexName.endsWith('_name_desc')) return 'name_desc';
  return 'default';
};

interface ProviderProps {
  dataset?: LocalDataset;
  /** The base browse products index (e.g. `dev-caroma-au`) — its order is the default "new first" view. */
  baseIndex: string;
  /** Refinements to seed on mount (e.g. restored from localStorage). */
  initialRefinementList?: Record<string, string[]>;
  children: React.ReactNode;
}

export const LocalDatasetProvider: React.FC<ProviderProps> = ({
  dataset,
  baseIndex,
  initialRefinementList,
  children,
}) => {
  const ssrDataset = dataset ?? EMPTY_DATASET;
  // The top-up route lives under the localised segment (`app/[locale]/api/plp-dataset`), so the URL
  // needs the same locale this page was rendered for. Falling back to the first path segment covers
  // any render where the route param is not populated — the request must never go to `/undefined/…`.
  const params = useParams();
  const routeLocale = typeof params?.locale === 'string' ? params.locale : '';

  // The hits withheld from the SSR payload, once the top-up has delivered them. Kept separate from
  // the prop so a re-render with the same (small) SSR dataset never discards them.
  const [extraHits, setExtraHits] = useState<AnyRecord[]>([]);
  const [isComplete, setIsComplete] = useState(!ssrDataset.topUp);

  const ds = useMemo<LocalDataset>(
    () => (extraHits.length === 0 ? ssrDataset : { ...ssrDataset, hits: ssrDataset.hits.concat(extraHits) }),
    [ssrDataset, extraHits],
  );

  const totalHits = ssrDataset.totalHits ?? ds.hits.length;

  // ---- Top-up: pull the withheld hits once, right after hydration ----
  // Deliberately eager rather than on-demand: facet counts, sort and the result count are all computed
  // from `hits`, so the dataset has to become whole on its own without waiting for the visitor to
  // interact. `topUp` is absent when the whole set already fitted in the first batch.
  //
  // Keyed on the SERIALISED descriptor, not the object: `plpDataset` is a prop, and depending on its
  // identity would re-run the whole fetch on any re-render that happened to hand us a fresh object.
  // The string only changes when the underlying query does (different listing, filter or facet set),
  // which is exactly when the remainder really must be pulled again.
  const topUpKey = ssrDataset.topUp ? JSON.stringify(ssrDataset.topUp) : '';
  useEffect(() => {
    if (!topUpKey) {
      setIsComplete(true);
      return;
    }
    let cancelled = false;
    setIsComplete(false);
    setExtraHits([]);

    (async () => {
      try {
        const localeSegment = routeLocale || window.location.pathname.split('/').filter(Boolean)[0] || '';
        // Trailing slash is required: the app runs with `trailingSlash: true`, so the un-slashed form
        // answers with a 308 before the handler is reached.
        const response = await fetch(`/${localeSegment}/api/plp-dataset/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: topUpKey,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const body = await response.json();
        if (cancelled) return;
        setExtraHits(Array.isArray(body?.hits) ? body.hits : []);
        setIsComplete(true);
      } catch (err) {
        if (cancelled) return;
        // Leave the grid on the SSR batch rather than emptying it. `isComplete` stays false, which is
        // what keeps the reveal controls from paging into hits that never arrived.
        console.error('[Algolia PLP] product top-up failed — showing the first batch only:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [topUpKey, routeLocale]);

  const [refinementList, setRefinementListState] = useState<Record<string, string[]>>(initialRefinementList ?? {});
  const [numericRefinements, setNumericRefinements] = useState<Record<string, [number, number]>>({});
  const [currentIndex, setCurrentIndexState] = useState<string>(baseIndex);

  const sortMode = indexToSortMode(currentIndex, baseIndex);

  // Log, once per dataset, whether the preloaded product data came from the Redis cache or Algolia —
  // and how the result set was split between the SSR payload and the top-up, which is the number this
  // whole path exists to keep small. Logged again when the top-up completes, so the console shows the
  // SSR weight and the full weight as two separate lines.
  const loggedCacheRef = useRef<string | null>(null);
  useEffect(() => {
    const info = ds.cacheInfo;
    if (!info) return;
    const key = `${ds.hits.length}|${info.source}|${info.cachedCount}/${info.algoliaCount}`;
    if (loggedCacheRef.current === key) return;
    loggedCacheRef.current = key;
    const cached = info.source === 'cache';
    const approxKb = (value: unknown) => {
      try {
        return Math.round(JSON.stringify(value).length / 1024);
      } catch {
        return 0;
      }
    };
    console.log(
      `%c[Algolia PLP] product data source: ${cached ? 'CACHE HIT (Redis)' : 'CACHE MISS (Algolia)'}`,
      `color:${cached ? '#16a34a' : '#dc2626'};font-weight:bold`,
      `· ${ds.hits.length}/${totalHits} products loaded · ~${approxKb(ds)}KB` +
        ` · in SSR payload: ${ssrDataset.hits.length} products ~${approxKb(ssrDataset)}KB` +
        ` · queries cached=${info.cachedCount} algolia=${info.algoliaCount}`,
    );
  }, [ds, ssrDataset, totalHits]);

  const results = useMemo(
    () => queryLocalDataset(ds, { refinementList, numericRefinements, sortMode, facets: '*' }),
    [ds, refinementList, numericRefinements, sortMode],
  );

  const toggleRefinement = useCallback((attribute: string, value: string) => {
    setRefinementListState((prev) => {
      const isColour = attribute.toLowerCase().includes('colour') || attribute.toLowerCase().includes('color');
      if (isColour) {
        const current = prev[attribute] ?? [];
        const exists = current.includes(value);
        const next = { ...prev };
        if (exists) {
          delete next[attribute];
        } else {
          next[attribute] = [value];
        }
        return next;
      }
      const current = prev[attribute] ?? [];
      const exists = current.includes(value);
      const nextValues = exists ? current.filter((v) => v !== value) : [...current, value];
      const next = { ...prev };
      if (nextValues.length > 0) next[attribute] = nextValues;
      else delete next[attribute];
      return next;
    });
  }, []);

  const setRefinementList = useCallback((next: Record<string, string[]>) => {
    setRefinementListState(next);
  }, []);

  const setNumericRefinement = useCallback((attribute: string, range: [number, number]) => {
    setNumericRefinements((prev) => ({ ...prev, [attribute]: range }));
  }, []);

  const clearNumericRefinement = useCallback((attribute: string) => {
    setNumericRefinements((prev) => {
      const next = { ...prev };
      delete next[attribute];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setRefinementListState({});
    setNumericRefinements({});
  }, []);

  const setCurrentIndex = useCallback((indexName: string) => {
    setCurrentIndexState(indexName);
  }, []);

  const value = useMemo<StoreShape>(
    () => ({
      dataset: ds,
      results,
      totalHits,
      isComplete,
      refinementList,
      numericRefinements,
      currentIndex,
      baseIndex,
      toggleRefinement,
      setRefinementList,
      setNumericRefinement,
      clearNumericRefinement,
      clearAll,
      setCurrentIndex,
    }),
    [
      ds,
      results,
      totalHits,
      isComplete,
      refinementList,
      numericRefinements,
      currentIndex,
      baseIndex,
      toggleRefinement,
      setRefinementList,
      setNumericRefinement,
      clearNumericRefinement,
      clearAll,
      setCurrentIndex,
    ],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

const useStore = (): StoreShape => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('Local PLP hooks must be used within <LocalDatasetProvider>');
  return ctx;
};

// ---------------------------------------------------------------------------
// InstantSearch-compatible shim hooks
// ---------------------------------------------------------------------------

/**
 * Mirrors useHits(): { hits, results }. These are the LOADED hits with the current refinements
 * applied — during the top-up window that is only the SSR batch, so anything reporting a total or
 * deciding how far the grid can be paged must read `useDatasetStatus()` rather than `hits.length`.
 */
export const useHits = () => {
  const { results } = useStore();
  return { hits: results.hits, results };
};

/**
 * Loading state of the two-phase dataset (no InstantSearch equivalent — this is ours).
 *
 * `totalHits` is the unfiltered size of the full result set, known from first paint. `isComplete`
 * says whether `hits` has caught up with it; while false, the grid is showing a prefix of the listing
 * and refinement-driven counts are provisional.
 *
 * `queryToken` identifies the current query — refinements plus sort order. It changes when, and only
 * when, the visitor asks for a different set of products, which makes it the correct trigger for
 * resetting the grid back to its first page (see useTileLoading). Topping the dataset up does not
 * change it, because that is the same query simply finishing.
 */
export const useDatasetStatus = () => {
  const { totalHits, isComplete, refinementList, numericRefinements, currentIndex } = useStore();

  const queryToken = useMemo(
    () => JSON.stringify([refinementList, numericRefinements, currentIndex]),
    [refinementList, numericRefinements, currentIndex],
  );

  return { totalHits, isComplete, queryToken };
};

interface RefinementListItem {
  value: string;
  label: string;
  /** InstantSearch facet items expose `name` (=== value); some sort comparators rely on it. */
  name: string;
  highlighted: string;
  count: number;
  isRefined: boolean;
}
type RefinementSortBy =
  // `any` params so InstantSearch-style comparators (typed over its FacetValue) are also accepted;
  // our items carry the same fields (value/label/name/count/isRefined) at runtime.
  | ((a: any, b: any) => number)
  /** InstantSearch token form, e.g. ['isRefined', 'count:desc', 'name:asc']. */
  | string[];

interface UseRefinementListProps {
  attribute: string;
  limit?: number;
  sortBy?: RefinementSortBy;
}

/** Build a comparator from InstantSearch sortBy tokens ('name'|'count'|'isRefined', optional :asc/:desc). */
const comparatorFromTokens =
  (tokens: string[]) =>
  (a: RefinementListItem, b: RefinementListItem): number => {
    for (const token of tokens) {
      const [field, dir = 'asc'] = token.split(':');
      let cmp = 0;
      if (field === 'name' || field === 'value' || field === 'label')
        cmp = String(a.name).localeCompare(String(b.name), undefined, { numeric: true });
      else if (field === 'count') cmp = a.count - b.count;
      else if (field === 'isRefined') cmp = (b.isRefined ? 1 : 0) - (a.isRefined ? 1 : 0);
      if (dir === 'desc') cmp = -cmp;
      if (cmp !== 0) return cmp;
    }
    return 0;
  };

/** Mirrors useRefinementList(): disjunctive option list + refine(value) toggle. */
export const useRefinementList = ({ attribute, limit = 999, sortBy }: UseRefinementListProps) => {
  const { results, refinementList, toggleRefinement } = useStore();

  const items = useMemo<RefinementListItem[]>(() => {
    const disjunctive = results.disjunctiveFacets.find((f) => f.name === attribute);
    const data = disjunctive?.data ?? results.facets[attribute] ?? {};
    const selected = new Set(refinementList[attribute] ?? []);
    let list: RefinementListItem[] = Object.entries(data).map(([value, count]) => ({
      value,
      label: value,
      name: value,
      highlighted: value,
      count,
      isRefined: selected.has(value),
    }));
    // Selected values must always remain visible even if their disjunctive count is 0.
    for (const value of selected) {
      if (!list.find((item) => item.value === value)) {
        list.push({ value, label: value, name: value, highlighted: value, count: 0, isRefined: true });
      }
    }
    if (sortBy) {
      const comparator = typeof sortBy === 'function' ? sortBy : comparatorFromTokens(sortBy);
      list = [...list].sort(comparator);
    }
    return list.slice(0, limit);
  }, [results, attribute, refinementList, limit, sortBy]);

  const refine = useCallback((value: string) => toggleRefinement(attribute, value), [attribute, toggleRefinement]);

  return { items, refine, canRefine: items.length > 0 };
};

/** Mirrors useRange(): { range, start, refine, canRefine }. Bounds derive from the dataset. */
export const useRange = ({ attribute }: { attribute: string }) => {
  const { dataset, numericRefinements, setNumericRefinement } = useStore();

  const range = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const hit of dataset.hits) {
      for (const v of valuesAt(hit, attribute)) {
        const n = typeof v === 'number' ? v : Number(v);
        if (Number.isFinite(n)) {
          min = Math.min(min, n);
          max = Math.max(max, n);
        }
      }
    }
    if (min === Infinity) return { min: 0, max: 0 };
    return { min, max };
  }, [dataset, attribute]);

  const current = numericRefinements[attribute];
  // Memoize by VALUE so the array reference is stable across renders — RangeFacet has a
  // useEffect keyed on `start`; a fresh array each render would loop ("Maximum update depth").
  const startMin = current ? current[0] : -Infinity;
  const startMax = current ? current[1] : Infinity;
  const start = useMemo<[number, number]>(() => [startMin, startMax], [startMin, startMax]);

  const refine = useCallback(
    ([min, max]: [number, number]) => setNumericRefinement(attribute, [min, max]),
    [attribute, setNumericRefinement],
  );

  return { range, start, refine, canRefine: range.min !== range.max };
};

interface CurrentRefinement {
  attribute: string;
  value: string;
  label: string;
  type: 'disjunctive' | 'numeric';
  operator?: string;
}
interface CurrentRefinementGroup {
  attribute: string;
  label: string;
  refinements: CurrentRefinement[];
}

/** Mirrors useCurrentRefinements(): grouped refinements + refine(refinement) to remove one. */
export const useCurrentRefinements = () => {
  const { refinementList, numericRefinements, toggleRefinement, clearNumericRefinement } = useStore();

  const items = useMemo<CurrentRefinementGroup[]>(() => {
    const groups: CurrentRefinementGroup[] = [];
    for (const attribute of Object.keys(refinementList)) {
      const refinements = (refinementList[attribute] ?? []).map<CurrentRefinement>((value) => ({
        attribute,
        value,
        label: value,
        type: 'disjunctive',
      }));
      if (refinements.length) groups.push({ attribute, label: attribute, refinements });
    }
    for (const attribute of Object.keys(numericRefinements)) {
      const [min, max] = numericRefinements[attribute];
      groups.push({
        attribute,
        label: attribute,
        refinements: [
          { attribute, value: String(min), label: String(min), type: 'numeric', operator: '>=' },
          { attribute, value: String(max), label: String(max), type: 'numeric', operator: '<=' },
        ],
      });
    }
    return groups;
  }, [refinementList, numericRefinements]);

  // Loosely typed so it also accepts InstantSearch's CurrentRefinementsConnectorParamsRefinement
  // (the copied product-list context passes that type through removeRefinement).
  const refine = useCallback(
    (refinement: { attribute: string; type?: string; value?: string | number | boolean }) => {
      if (refinement.type === 'numeric') clearNumericRefinement(refinement.attribute);
      else toggleRefinement(refinement.attribute, String(refinement.value));
    },
    [toggleRefinement, clearNumericRefinement],
  );

  return { items, refine };
};

/** Mirrors useClearRefinements(): { refine } clears everything. */
export const useClearRefinements = () => {
  const { clearAll } = useStore();
  return { refine: clearAll, canRefine: true };
};

/**
 * The listing's base products index (caroma-au / caroma-nz): the value of the default "New" sort option;
 * `<base>_name_asc` / `<base>_name_desc` are the name sorts (no InstantSearch equivalent — this is ours).
 */
export const useBaseIndex = () => useStore().baseIndex;

/** Mirrors useSortBy(): switches the active "index" (which maps to a local sort mode). */
export const useSortBy = ({ items }: { items: SortItem[] }) => {
  const { currentIndex, setCurrentIndex } = useStore();
  const refine = useCallback((value: string) => setCurrentIndex(value), [setCurrentIndex]);
  return { currentRefinement: currentIndex, options: items, refine };
};

/**
 * Mirrors the subset of useInstantSearch() the PLP uses: status, results, and setUiState
 * (used to restore refinementList from localStorage). uiState is keyed by index name and we
 * only read its refinementList for the current/base index.
 */
export const useInstantSearch = () => {
  const { results, refinementList, setRefinementList, currentIndex, baseIndex } = useStore();

  const setUiState = useCallback(
    (updater: any) => {
      const prevUi = { [baseIndex]: { refinementList }, [currentIndex]: { refinementList } };
      const nextUi = typeof updater === 'function' ? updater(prevUi) : updater;
      const next = nextUi?.[currentIndex]?.refinementList ?? nextUi?.[baseIndex]?.refinementList ?? null;
      if (next) setRefinementList(next);
    },
    [refinementList, setRefinementList, currentIndex, baseIndex],
  );

  return { status: 'idle' as const, results, setUiState };
};
